import sortBy from 'lodash/sortBy';
import api from '../plugins/axios';
import { allowedPrice } from '@/utils/utils';
import gtmEventTracker from '@/utils/gtmEventTracker';

function handleError(error) {
  console.error('API Error:', error);
}

export default {
  state: {
    global: {},
    notifications: {},
    personal_notifications: {},
    users: [],
    loading: true,
    logoLoading: false,
    rewards: [],
    purchases: [],
    purchasesData: {
      purchases: [],
      users: [],
      cursor: null,
    },
    payment_method: null,
  },
  getters: {
    sortedUsers(state) {
      const states = ['pending', 'accepted', 'deactivated'];
      return sortBy(state.users, [(s) => states.indexOf(s.state), 'name']);
    },
    hasDemo(state) {
      return state.users.some((user) => user.demo);
    },
    loading(state) {
      return state.loading;
    },
    sortedRewards(state) {
      const sortedRewards = state.rewards
        .filter((reward) => allowedPrice(reward) > 0)
        .slice()
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .reduce((acc, reward) => {
          const category = reward.category || 'uncategorised';
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(reward);
          return acc;
        }, {});
      return sortedRewards;
    },
    sortedPurchasesData(state) {
      const purchasesArray = [...state.purchasesData.purchases];
      purchasesArray.forEach((p) => {
        p.users = [];

        p.user_ids.forEach((userId, index) => {
          const user = state.purchasesData.users.find((el) => el.id === userId);
          const purchUser = { ...user };
          purchUser.created_at = p.dates[index];
          purchUser.comment = p.comments[index];
          p.users.push(purchUser);
        });
      });
      return purchasesArray.sort((a, b) => {
        if (a.status === 'pending' && b.status !== 'pending') {
          return -1;
        }
        if (a.status !== 'pending' && b.status === 'pending') {
          return 1;
        }
        return new Date(b.status_changed_at) - new Date(a.status_changed_at);
      });
    },
    purchasesCursor(state) {
      return state.purchasesData ? state.purchasesData.cursor : null;
    },
  },
  actions: {
    async loadSettings({ commit }) {
      try {
        commit('setLoading', true);
        const { data } = await api.get('/settings');
        commit('setState', data);
        commit('setLoading', false);
      } catch (e) {
        handleError(e);
        throw e;
      }
    },
    async loadUsers({ commit }) {
      try {
        const resp = await api.get('/users');
        commit('setState', { users: resp.data });
        return resp.data;
      } catch (e) {
        handleError(e);
      }
    },
    async addUsers({ state, commit }, users) {
      try {
        const { data } = await api.post('/users', { users });
        if (data.length > 0) commit('setState', { users: [...data, ...state.users] });
        return data.length;
      } catch (e) {
        handleError(e);
        return 0;
      }
    },
    // eslint-disable-next-line no-empty-pattern
    async exportUsers({}, state = '') {
      const filter = state ? `?state=${state}` : '';
      const path = `/users.csv${filter}`;
      const { data } = await api.get(path, { responseType: 'blob' });
      return data;
    },
    async updatePermissions({ dispatch }, user) {
      const { permissions } = user;
      const resp = await api.put(`/users/${user.id}/permissions`, permissions);
      dispatch('updateUser', resp.data);
    },
    async deactivateUser({ dispatch }, user) {
      await api.delete(`/users/${user.id}`);
      user.state = 'deactivated';
      dispatch('updateUser', user);
    },
    async reactivateUser({ dispatch }, user) {
      await api.post(`/users/${user.id}/invite`);
      user.state = 'pending';
      dispatch('updateUser', user);
    },
    async getUser({ dispatch }, userId) {
      const { data } = await api.get(`/users/${userId}`);
      dispatch('updateUser', data);
      return data;
    },
    async editUser({ dispatch }, user) {
      const resp = await api.put(`/users/${user.id}`, user);
      dispatch('updateUser', resp.data);
    },
    async updateNotifications({ commit }, params) {
      try {
        commit('setLoading', true);
        const { data } = await api.put('/settings/notifications', { ...params });
        commit('setState', data);
        commit('setLoading', false);
      } catch (e) {
        handleError(e);
      }
    },
    async updateSettings({ commit, dispatch }, global) {
      try {
        commit('setLoading', true);
        const { data } = await api.put('/settings', { ...global });
        commit('setState', data);
        commit('setLoading', false);
        commit('auth/setCurrentSettings', data.global, { root: true });
        commit('auth/updateCurrentWorkspace', { name: data.global.name }, { root: true });
        await dispatch('auth/getWorkspace', null, { root: true });
      } catch (e) {
        handleError(e);
      }
    },
    async loadValues({ commit }) {
      try {
        const { data } = await api.get('/values');
        commit('setState', { values: data });
      } catch (e) {
        handleError(e);
      }
    },
    async addValue({ commit }, value) {
      try {
        const { data } = await api.post('/values', value);
        commit('auth/addValue', data, { root: true });
        return data;
      } catch (e) {
        return { errors: e.response.data };
      }
    },
    async updateValue({ commit }, value) {
      try {
        const { data } = await api.put(`/values/${value.id}`, value);
        commit('auth/updateValue', data, { root: true });
        return data;
      } catch (e) {
        return { errors: e.response.data };
      }
    },
    async deleteValue({ commit, dispatch }, value) {
      try {
        await api.delete(`/values/${value.id}`);
        if (value.default) {
          dispatch('auth/loadValues', null, { root: true });
        } else {
          commit('auth/deleteValue', value, { root: true });
        }
      } catch (e) {
        handleError(e);
      }
    },
    async updateLogo({ commit, dispatch }, logo) {
      try {
        commit('setLogoLoading', true);
        const { data } = await api.post('/settings/logo', { logo }, { headers: { 'Content-Type': 'multipart/form-data' } });
        commit('setState', data);
        commit('auth/updateCurrentWorkspace', { logo: data.global.logo }, { root: true });
        await dispatch('auth/getWorkspace', null, { root: true });
      } catch (e) {
        handleError(e);
        throw e;
      } finally {
        commit('setLogoLoading', false);
      }
    },
    async resetAccount({ commit }, options) {
      try {
        commit('setLoading', true);
        await api.delete('/workspace/reset', { data: { data: options } });
      } catch (e) {
        handleError(e);
        throw e;
      } finally {
        commit('setLoading', false);
      }
    },
    async destroyAccount() {
      try {
        await api.delete('/workspace');
      } catch (e) {
        handleError(e);
      }
    },
    async loadRewards({ commit }) {
      try {
        const { data } = await api.get('/rewards/rewards');
        commit('setRewards', data);
      } catch (e) {
        handleError(e);
        throw e;
      }
    },
    async addReward({ state, commit, dispatch }, reward) {
      try {
        const { data } = await api.post('/rewards/rewards', reward, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        commit('addReward', data);
        if (state.rewards.length === 1) {
          await dispatch('shop/loadRewards', null, { root: true });
        }
      } catch (e) {
        handleError(e);
      }
    },
    async updateReward({ commit }, reward) {
      try {
        const { data } = await api.put(`/rewards/rewards/${reward.id}`, reward, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        commit('updateReward', data);
      } catch (e) {
        handleError(e);
      }
    },
    async cloneReward({ commit }, rewardData) {
      try {
        const { data } = await api.post(`/rewards/rewards/${rewardData.id}/duplicate`, rewardData);
        commit('addReward', data);
      } catch (e) {
        handleError(e);
      }
    },
    async deleteReward({ state, commit, dispatch }, rewardId) {
      try {
        await api.delete(`/rewards/rewards/${rewardId}`);
        commit('deleteReward', rewardId);
        if (!state.rewards.length) {
          await dispatch('shop/loadRewards', null, { root: true });
        }
      } catch (e) {
        handleError(e);
      }
    },
    async adjustBalance({ dispatch }, adjust) {
      let response = {};
      try {
        const { data } = await api.put(`/coins/${adjust.id}`, adjust);
        dispatch('updateUser', { id: adjust.id, balance: data.balance });
        response = data;
      } catch (e) {
        handleError(e);
        response.errors = [{ message: 'invalid_amount' }];
      }
      return response;
    },
    async initPurchases({ state, dispatch }) {
      Object.assign(state.purchasesData, { purchases: [], users: [], cursor: null });
      await dispatch('loadPurchases');
    },
    async loadPurchases({ state, commit }, status = 'all') {
      let endpoint = `/rewards/purchases?status=${status}`;
      if (state.purchasesData.cursor) {
        endpoint += `&cursor=${state.purchasesData.cursor}`;
      }
      try {
        const { data } = await api.get(endpoint);
        commit('addPurchasesData', data);
      } catch (e) {
        handleError(e);
        throw e;
      }
    },
    async fulfillPurchase({ commit }, purchaseId) {
      try {
        const { data } = await api.post(`/rewards/purchases/${purchaseId}`);
        gtmEventTracker('manual_reward_fulfilled', 'Manual_reward', 'fulfilled', 'Manual reward fulfilled');
        commit('updatePurchase', data.purchases[0]);
      } catch (e) {
        handleError(e);
        throw e;
      }
    },
    async declinePurchase({ commit, dispatch }, purchaseId) {
      try {
        const { data } = await api.delete(`/rewards/purchases/${purchaseId}`);
        commit('updatePurchase', data.purchases[0]);
        gtmEventTracker('manual_reward_declined', 'Manual_reward', 'declined', 'Manual reward declined');
        await dispatch('loadRewards');
      } catch (e) {
        handleError(e);
        throw e;
      }
    },
    async addRewardsCard() {
      const url = window.location.origin + '/settings/rewards/all?active_tab=billing';
      try {
        const resp = await api.post('/rewards/payment_method', { callback: url });
        gtmEventTracker('reward_card_added', 'Reward_card_added', 'Reward_card_added', 'Reward card added');
        window.location.assign(resp.data.session_url);
      } catch (e) {
        handleError(e);
      }
    },
    async removeRewardsCard({ commit }) {
      try {
        await api.delete('/rewards/payment_method');
        commit('setPaymentMethod', { payment_method: null });
      } catch (e) {
        handleError(e);
      }
    },
    async getRewardsCard({ commit }) {
      try {
        const resp = await api.get('/rewards/payment_method');
        commit('setPaymentMethod', resp.data);
      } catch (e) {
        handleError(e);
      }
    },
    updateUser({ commit, rootState }, userData) {
      commit('updateUser', userData);
      if (rootState.auth.currentUser.id === userData.id) commit('auth/setCurrentUser', userData, { root: true });
    },
  },
  mutations: {
    setState(state, data) {
      Object.assign(state, data);
    },
    updateUser(state, data) {
      const updatedUser = state.users.find((user) => user.id === data.id);
      if (updatedUser) Object.assign(updatedUser, data);
    },
    setLoading(state, value) {
      state.loading = value;
    },
    setLogoLoading(state, value) {
      state.logoLoading = value;
    },
    setRewards(state, rewards) {
      state.rewards = rewards;
    },
    addReward(state, reward) {
      state.rewards.push(reward);
    },
    updateReward(state, reward) {
      const updatedReward = state.rewards.find((el) => el.id === reward.id);
      if (updatedReward) {
        Object.assign(updatedReward, reward);
      }
    },
    deleteReward(state, rewardId) {
      const currentIndex = state.rewards.findIndex((el) => el.id === rewardId);
      if (currentIndex !== -1) {
        state.rewards.splice(currentIndex, 1);
      }
    },
    addPurchasesData(state, data) {
      state.purchasesData.purchases.push(...data.purchases);
      state.purchasesData.users.push(...data.users);
      state.purchasesData.cursor = data.cursor.cursor;
    },
    updatePurchase(state, purchase) {
      const prevPurchase = state.purchasesData.purchases.find((p) => p.id === purchase.id);
      if (prevPurchase) {
        Object.assign(prevPurchase, purchase);
      }
    },
    setPaymentMethod(state, data) {
      state.payment_method = data.payment_method;
    },
  },
  namespaced: true,
};
